import { DocumentSection, EntityDocument } from '@toggle/toggle';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Markdown } from '~/components/markdown/Markdown';
import { DocumentProvider } from '~/modules/document-provider/DocumentProvider';
import { useDocuments } from '~/react-query-hooks/documents/useDocumentsEntity';

import * as S from './Citations.styles';

export interface CitationsProps {
  citations: DocumentSection[];
  onClick: (citationId: string, document: EntityDocument) => void;
  activeIdx?: number;
  ref?: React.RefObject<(HTMLDivElement | null)[]>;
}

export const Citations = ({
  citations,
  onClick,
  activeIdx,
  ref,
}: CitationsProps) => {
  const { t } = useTranslation(['widget']);

  const { data } = useDocuments(citations.map(c => c.document));

  useEffect(() => {
    if (activeIdx !== undefined) {
      const activeRef = ref?.current?.[activeIdx];
      activeRef?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, []);

  return (
    <>
      <S.Header>{t('widget:citations.passage')}</S.Header>
      {citations.map((citation, idx) => {
        const document = data.find(d => d?.id === citation.document);

        if (!document) {
          return null;
        }

        const { kind, period, year } = document;

        return (
          <S.Container
            key={idx}
            ref={(el: HTMLDivElement | null) => {
              if (ref?.current) {
                ref.current[idx] = el;
              }
            }}
            onClick={() => onClick(citation.id, document)}
            $active={idx === activeIdx}
            data-testid={`citation-${idx}`}
          >
            <S.MarkdownWrapper>
              <Markdown message={`"${citation.content}"`} />
            </S.MarkdownWrapper>
            <DocumentProvider
              documentKind={kind}
              date={`${year} ${period.toUpperCase()}`}
            />
          </S.Container>
        );
      })}
    </>
  );
};
